import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { toast } from 'react-hot-toast';
import { FiDownload, FiFilter, FiRefreshCw, FiSearch, FiX } from 'react-icons/fi';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader, StateBlock, Skeleton } from '../../components/UI';
import Dialog from '../../components/UI/Dialog';
import MatchRow from '../../components/UI/MatchRow';
import MapThumb from '../../components/UI/MapThumb';
import { matchState, matchTitle } from '../../components/UI/matchLabels';
import { Action, Field, Select } from '../MVP/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAsync } from '../../hooks/useAsync';
import { useFormat } from '../../hooks/useFormat';
import { apiFetch } from '../../lib/api';
import { displayMap, formatDuration, pluralRu } from '../../lib/format';
import { buildStatsQueryParams } from '../../lib/statsFilters';
import { formatChartDate, matchOutcome, type StatsMatch } from './statsUtils';

const DEFAULT_MAP_OPTIONS = ['dust2', 'mirage', 'inferno', 'nuke', 'overpass', 'ancient', 'anubis', 'vertigo'];
const PAGE_SIZE = 10;
const PERIODS = ['7', '30', '90', 'all'] as const;

type MeSummary = {
  total_matches: number;
  wins: number;
  losses: number;
  draws: number;
  win_rate: number;
  kills: number;
  deaths: number;
  kd: number;
  elo: number;
};
type ChartsData = {
  by_map: { map: string; wins: number; losses: number; win_rate: number }[];
  by_day: { date: string; matches: number; wins: number; losses: number }[];
  elo_history: { date: string; elo: number }[];
};
type MatchesPage = { items: StatsMatch[]; total: number; totalPages: number };
type Query = { map: string; dateRange: string; result: string; q: string; page: number };
const DEFAULT_QUERY: Query = { map: 'all', dateRange: '30', result: 'all', q: '', page: 1 };

/* ───────────────────────── раскладка ───────────────────────── */
const Page = styled.div`
  width: 100%;
  min-width: 0;
  display: grid;
  gap: 24px;
  @media (max-width: 767px) {
    gap: 16px;
  }
`;
const Surface = styled.section`
  min-width: 0;
  padding: 24px;
  border-radius: 16px;
  background: ${(p) => p.theme.colors.surface};
  @media (max-width: 767px) {
    padding: 16px;
  }
  h2 {
    font-size: 18px;
    line-height: 26px;
    font-weight: 600;
  }
  .hint {
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  .head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 4px 16px;
    margin-bottom: 16px;
  }
`;

/* ───────────────────────── фильтры ───────────────────────── */
const Filters = styled.form`
  display: grid;
  gap: 12px;
  .bar {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px 16px;
  }
  .field {
    display: grid;
    gap: 6px;
    min-width: 0;
    flex: 1 1 160px;
    font-size: 13px;
    font-weight: 500;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .inline-only {
    display: contents;
  }
  .mobile-only {
    display: none;
  }
  @media (max-width: 767px) {
    .inline-only {
      display: none;
    }
    .mobile-only {
      display: inline-flex;
    }
  }
`;
const Segmented = styled.div`
  display: inline-flex;
  padding: 3px;
  border-radius: 10px;
  background: ${(p) => p.theme.colors.input};
  button {
    min-width: 56px;
    min-height: 40px;
    padding: 0 14px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.textSecondary};
    transition:
      background ${(p) => p.theme.motion.fast},
      color ${(p) => p.theme.motion.fast};
    &:hover {
      color: ${(p) => p.theme.colors.text};
    }
    &[aria-pressed='true'] {
      background: ${(p) => p.theme.colors.surfaceHover};
      color: ${(p) => p.theme.colors.text};
    }
  }
  @media (max-width: 767px) {
    flex: 1;
    button {
      flex: 1;
      min-width: 0;
      padding: 0 8px;
    }
  }
`;
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 36px;
    padding: 0 6px 0 12px;
    border-radius: 18px;
    background: ${(p) => p.theme.colors.input};
    font-size: 13px;
    font-weight: 500;
    button {
      display: grid;
      place-items: center;
      width: 28px;
      height: 28px;
      border-radius: 14px;
      color: ${(p) => p.theme.colors.textSecondary};
      &:hover {
        background: ${(p) => p.theme.colors.surfaceHover};
        color: ${(p) => p.theme.colors.text};
      }
    }
  }
  .reset {
    min-height: 36px;
    padding: 0 10px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.primary};
    &:hover {
      background: ${(p) => p.theme.colors.primarySoft};
    }
  }
`;

/* ───────────────────────── показатели ───────────────────────── */
const Kpis = styled.dl`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 24px;
  > div {
    min-width: 0;
    display: grid;
    gap: 2px;
  }
  dt {
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  dd.v {
    font-size: 32px;
    line-height: 40px;
    font-weight: 700;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    min-height: 40px;
  }
  dd.v.none {
    font-size: 20px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.textMuted};
  }
  dd.h {
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  @media (max-width: 767px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px 16px;
    dd.v {
      font-size: 28px;
    }
  }
`;

/* ───────────────────────── графики ───────────────────────── */
const Split = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 24px;
  align-items: start;
  > * {
    min-width: 0;
  }
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const ChartBox = styled.div`
  height: 260px;
  width: 100%;
  @media (max-width: 767px) {
    height: 210px;
  }
`;
const Empty = styled.p`
  display: grid;
  place-items: center;
  min-height: 120px;
  text-align: center;
  font-size: 14px;
  color: ${(p) => p.theme.colors.textMuted};
`;
const Outcomes = styled.div`
  display: grid;
  gap: 16px;
  .bar {
    display: flex;
    height: 12px;
    gap: 2px;
    border-radius: 6px;
    overflow: hidden;
    background: ${(p) => p.theme.colors.input};
    span {
      display: block;
      height: 100%;
    }
  }
  ul {
    list-style: none;
    display: grid;
    gap: 10px;
  }
  li {
    display: grid;
    grid-template-columns: 12px 1fr auto auto;
    align-items: center;
    gap: 10px;
    font-size: 15px;
    i {
      width: 12px;
      height: 12px;
      border-radius: 3px;
    }
    .n {
      font-weight: 700;
      font-variant-numeric: tabular-nums;
    }
    .p {
      min-width: 4ch;
      text-align: right;
      font-size: 13px;
      color: ${(p) => p.theme.colors.textMuted};
      font-variant-numeric: tabular-nums;
    }
  }
`;
const MapRows = styled.ul`
  list-style: none;
  display: grid;
  gap: 4px;
  li {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) minmax(0, 1.2fr);
    align-items: center;
    gap: 12px;
    min-height: 56px;
    padding: 6px 0;
  }
  .name {
    font-size: 15px;
    line-height: 20px;
    font-weight: 600;
    small {
      display: block;
      font-size: 13px;
      font-weight: 400;
      color: ${(p) => p.theme.colors.textMuted};
    }
  }
  .meter {
    display: grid;
    gap: 4px;
    justify-items: end;
    font-size: 14px;
    font-weight: 650;
    font-variant-numeric: tabular-nums;
    i {
      display: block;
      width: 100%;
      height: 6px;
      border-radius: 3px;
      background: ${(p) => p.theme.colors.input};
      overflow: hidden;
      b {
        display: block;
        height: 100%;
        background: ${(p) => p.theme.colors.primary};
      }
    }
  }
`;
const Pager = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  margin-top: 12px;
  font-size: 14px;
  color: ${(p) => p.theme.colors.textMuted};
  .btns {
    display: flex;
    gap: 8px;
  }
`;
const Rows = styled.ul`
  list-style: none;
  display: grid;
  gap: 2px;
  margin: 0 -8px;
`;
const Start = styled.div`
  display: grid;
  justify-items: start;
  gap: 8px;
  h2 {
    font-size: 20px;
  }
  p {
    max-width: 52ch;
    color: ${(p) => p.theme.colors.textSecondary};
    font-size: 15px;
    line-height: 22px;
  }
`;

async function getJson<T>(path: string, signal: AbortSignal): Promise<T> {
  const res = await apiFetch(path, { signal });
  if (!res.ok) throw new Error(String(res.status));
  return (await res.json()) as T;
}

const Stats: React.FC = () => {
  const { t, language } = useLanguage();
  const f = useFormat();
  const theme = useTheme();
  const [query, setQuery] = useState<Query>(DEFAULT_QUERY);
  const [searchInput, setSearchInput] = useState('');
  const [sheet, setSheet] = useState(false);
  // Любое изменение фильтра сбрасывает страницу одним обновлением состояния.
  const update = (patch: Partial<Omit<Query, 'page'>>) => setQuery((q) => ({ ...q, ...patch, page: 1 }));

  useEffect(() => {
    const id = window.setTimeout(() => {
      const next = searchInput.trim();
      setQuery((q) => (q.q === next ? q : { ...q, q: next, page: 1 }));
    }, 400);
    return () => window.clearTimeout(id);
  }, [searchInput]);

  const params = useMemo(() => buildStatsQueryParams(query, query.q), [query]);
  const filterKey = params.toString();
  const suffix = filterKey ? `?${filterKey}` : '';
  const summary = useAsync<MeSummary>(`summary|${filterKey}`, (signal) => getJson<MeSummary>(`/stats/me/summary${suffix}`, signal));
  const charts = useAsync<ChartsData>(`charts|${filterKey}`, (signal) => getJson<ChartsData>(`/stats/me/charts${suffix}`, signal));
  const list = useAsync<MatchesPage>(`list|${filterKey}|${query.page}`, async (signal) => {
    const p = new URLSearchParams({ page: String(query.page), pageSize: String(PAGE_SIZE) });
    params.forEach((value, key) => p.set(key, value));
    const data: any = await getJson(`/stats/me/matches?${p.toString()}`, signal);
    const items: StatsMatch[] = (data?.items || []).map((m: any) => ({
      id: m.id,
      map: m.map,
      mode: m.mode,
      server: m.server,
      type: m.type,
      status: m.status,
      players: [],
      score: [m.score_ct ?? 0, m.score_t ?? 0],
      // Сервер пока не возвращает длительность: неизвестное значение ≠ 0.
      duration: typeof m.duration === 'number' ? m.duration : null,
      createdAt: typeof m.created_at === 'string' ? m.created_at : new Date(m.created_at).toISOString(),
      team: m.team,
      kills: m.kills,
      deaths: m.deaths,
      assists: m.assists,
      playerScore: m.score,
    }));
    return { items, total: data?.total ?? items.length, totalPages: Math.max(1, data?.totalPages ?? 1) };
  });

  const s = summary.data;
  const kpiLoading = summary.status === 'loading' || summary.status === 'idle';
  const mapOptions = useMemo(() => {
    const fromCharts = (charts.data?.by_map ?? []).map((m) => m.map.toLowerCase().replace(/^de_/, ''));
    return Array.from(new Set([...DEFAULT_MAP_OPTIONS, ...fromCharts]));
  }, [charts.data]);

  const periodLabel = t(
    ({ '7': 'stats.filter.last7', '30': 'stats.filter.last30', '90': 'stats.filter.last90', all: 'stats.filter.all_time' } as Record<string, string>)[query.dateRange],
  );
  const periodShort = (p: string) => (p === 'all' ? (language === 'ru' ? 'Всё' : 'All') : language === 'ru' ? `${p} дн.` : `${p}d`);
  const dirty = query.map !== 'all' || query.dateRange !== '30' || query.result !== 'all' || !!query.q;
  const pageData = list.status === 'success' ? list.data : null;
  const busy = list.status === 'loading' || summary.status === 'loading' || charts.status === 'loading';
  const activeCount = (query.map !== 'all' ? 1 : 0) + (query.result !== 'all' ? 1 : 0) + (query.q ? 1 : 0);
  const newbie = summary.status === 'success' && s?.total_matches === 0 && !dirty && pageData?.total === 0;

  function reset() {
    setSearchInput('');
    setQuery(DEFAULT_QUERY);
  }
  function refresh() {
    summary.reload();
    charts.reload();
    list.reload();
  }
  function exportPage() {
    if (!pageData || pageData.items.length === 0) return;
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const headers = ['id', 'date', 'map', 'mode', 'server', 'status', 'team', 'score_ct', 'score_t', 'kills', 'deaths', 'assists'];
    const lines = [headers.join(',')];
    for (const m of pageData.items) {
      lines.push(
        [esc(m.id), esc(new Date(m.createdAt).toISOString()), esc(m.map), esc(m.mode), esc(m.server), esc(m.status), esc(m.team ?? ''), m.score[0], m.score[1], m.kills ?? '', m.deaths ?? '', m.assists ?? ''].join(','),
      );
    }
    // BOM, чтобы Excel корректно открыл UTF-8.
    const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `matches-${query.dateRange === 'all' ? 'all' : query.dateRange + 'd'}-page-${query.page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t('stats.export_done'));
  }

  /* ───── данные графиков: только реальные точки, без сглаживания и дорисовки ───── */
  const eloData = useMemo(() => (charts.data?.elo_history ?? []).map((p) => ({ date: formatChartDate(p.date, language === 'ru' ? 'ru-RU' : 'en-US'), elo: p.elo })), [charts.data, language]);
  const dayData = useMemo(() => (charts.data?.by_day ?? []).map((d) => ({ date: formatChartDate(d.date, language === 'ru' ? 'ru-RU' : 'en-US'), matches: d.matches })), [charts.data, language]);
  const mapRows = useMemo(
    () => (charts.data?.by_map ?? []).map((m) => ({ id: m.map, name: displayMap(m.map), n: m.wins + m.losses, rate: Math.round(m.win_rate) })),
    [charts.data],
  );
  const tooltipStyle = {
    backgroundColor: theme.colors.surfaceHover,
    border: `1px solid ${theme.colors.borderLight}`,
    borderRadius: 10,
    color: theme.colors.text,
  };
  const matchesWord = (n: number) => (language === 'ru' ? pluralRu(n, ['матч', 'матча', 'матчей']) : n === 1 ? 'match' : 'matches');

  const total = s ? s.wins + s.losses + s.draws : 0;
  const seg = s
    ? [
        { key: 'win', label: t('stats.wins_short'), n: s.wins, color: theme.colors.success },
        { key: 'loss', label: t('stats.losses_short'), n: s.losses, color: theme.colors.danger },
        { key: 'draw', label: t('stats.draws_short'), n: s.draws, color: theme.colors.textMuted },
      ]
    : [];

  const filterFields = (
    <>
      <label className="field">
        {t('stats.filter.map')}
        <Select value={query.map} onChange={(e) => update({ map: e.target.value })}>
          <option value="all">{t('stats.filter.all_maps')}</option>
          {mapOptions.map((m) => (
            <option key={m} value={m}>
              {displayMap(m)}
            </option>
          ))}
        </Select>
      </label>
      <label className="field">
        {t('stats.filter.result')}
        <Select value={query.result} onChange={(e) => update({ result: e.target.value })}>
          <option value="all">{t('stats.filter.all_matches')}</option>
          <option value="win">{t('stats.filter.only_wins')}</option>
          <option value="loss">{t('stats.filter.only_losses')}</option>
          <option value="draw">{t('stats.filter.only_draws')}</option>
        </Select>
      </label>
      <label className="field">
        {t('common.search')}
        <span style={{ position: 'relative', display: 'block' }}>
          <Field type="search" value={searchInput} placeholder={t('stats.table.search_placeholder')} onChange={(e) => setSearchInput(e.target.value)} style={{ paddingLeft: 40 }} />
          <FiSearch aria-hidden="true" style={{ position: 'absolute', left: 14, top: 16, opacity: 0.6 }} />
        </span>
      </label>
    </>
  );

  return (
    <Page>
      <PageHeader
        title={t('stats.title')}
        actions={
          <>
            <Action type="button" onClick={exportPage} disabled={!pageData || pageData.items.length === 0}>
              <FiDownload aria-hidden="true" />
              {t('stats.export_page')}
            </Action>
            <Action type="button" onClick={refresh} disabled={busy} aria-busy={busy}>
              <FiRefreshCw aria-hidden="true" />
              {t('stats.refresh')}
            </Action>
          </>
        }
      />

      {newbie ? (
        <Surface>
          <Start>
            <h2>{t('stats.empty_title')}</h2>
            <p>{t('stats.empty_text', { elo: f.number(s?.elo, 0) })}</p>
            <Action as={Link} to="/" $variant="primary" style={{ marginTop: 8 }}>
              {t('stats.empty_cta')}
            </Action>
          </Start>
        </Surface>
      ) : (
        <>
          <Surface aria-label={t('stats.filters')}>
            <Filters
              role="search"
              aria-label={t('stats.filters')}
              onSubmit={(e) => {
                e.preventDefault();
                setQuery((q) => ({ ...q, q: searchInput.trim(), page: 1 }));
              }}
            >
              <div className="bar">
                <div style={{ display: 'grid', gap: 6, minWidth: 0, flex: '0 1 auto' }} className="period">
                  <span className="hint" id="period-label">
                    {t('stats.period_label')}
                  </span>
                  <Segmented role="group" aria-labelledby="period-label">
                    {PERIODS.map((p) => (
                      <button key={p} type="button" aria-pressed={query.dateRange === p} onClick={() => update({ dateRange: p })}>
                        {periodShort(p)}
                      </button>
                    ))}
                  </Segmented>
                </div>
                <div className="inline-only">{filterFields}</div>
                <Action type="button" className="mobile-only" onClick={() => setSheet(true)}>
                  <FiFilter aria-hidden="true" />
                  {t('stats.filters_btn')}
                  {activeCount > 0 && <span> · {activeCount}</span>}
                </Action>
              </div>
              {dirty && (
                <Chips aria-label={t('stats.filters')}>
                  {query.map !== 'all' && (
                    <span className="chip">
                      {displayMap(query.map)}
                      <button type="button" aria-label={t('stats.remove_filter', { name: displayMap(query.map) })} onClick={() => update({ map: 'all' })}>
                        <FiX aria-hidden="true" />
                      </button>
                    </span>
                  )}
                  {query.result !== 'all' && (
                    <span className="chip">
                      {t(`stats.filter.${query.result === 'win' ? 'only_wins' : query.result === 'loss' ? 'only_losses' : 'only_draws'}`)}
                      <button type="button" aria-label={t('stats.remove_filter', { name: t('stats.filter.result') })} onClick={() => update({ result: 'all' })}>
                        <FiX aria-hidden="true" />
                      </button>
                    </span>
                  )}
                  {query.q && (
                    <span className="chip">
                      «{query.q}»
                      <button
                        type="button"
                        aria-label={t('stats.remove_filter', { name: t('common.search') })}
                        onClick={() => {
                          setSearchInput('');
                          update({ q: '' });
                        }}
                      >
                        <FiX aria-hidden="true" />
                      </button>
                    </span>
                  )}
                  <button type="button" className="reset" onClick={reset}>
                    {t('stats.reset_filters')}
                  </button>
                </Chips>
              )}
              <p className="hint">{t('stats.scope', { period: periodLabel.toLowerCase() })}</p>
            </Filters>
          </Surface>

          <Surface aria-busy={kpiLoading}>
            {summary.status === 'error' ? (
              <StateBlock kind="error" title={t('stats.summary_error')} onAction={summary.reload} />
            ) : (
              <Kpis>
                <div>
                  <dt>{t('stats.total_matches')}</dt>
                  <dd className="v">{kpiLoading ? <Skeleton $h={30} $w="60%" /> : f.number(s?.total_matches, 0)}</dd>
                  <dd className="h">{t('stats.kpi_matches_hint')}</dd>
                </div>
                <div>
                  <dt>{t('stats.winrate')}</dt>
                  {kpiLoading ? (
                    <dd className="v"><Skeleton $h={30} $w="60%" /></dd>
                  ) : s && s.total_matches > 0 ? (
                    <dd className="v">{f.percent(s.win_rate, 1)}</dd>
                  ) : (
                    <dd className="v none">{t('stats.no_data')}</dd>
                  )}
                  <dd className="h">{t('stats.winrate_hint')}</dd>
                </div>
                <div>
                  <dt>{t('stats.kd_ratio')}</dt>
                  {kpiLoading ? (
                    <dd className="v"><Skeleton $h={30} $w="60%" /></dd>
                  ) : s && s.deaths > 0 ? (
                    <dd className="v">{f.number(s.kd, 2)}</dd>
                  ) : (
                    <dd className="v none">{t('stats.no_data')}</dd>
                  )}
                  <dd className="h">{t('stats.kd_hint')}</dd>
                </div>
                <div>
                  <dt>{t('stats.current_elo')}</dt>
                  <dd className="v">{kpiLoading ? <Skeleton $h={30} $w="60%" /> : f.number(s?.elo, 0)}</dd>
                  <dd className="h">{t('stats.current_elo_hint')}</dd>
                </div>
              </Kpis>
            )}
          </Surface>

          <Split>
            <Surface>
              <div className="head">
                <h2>{t('stats.elo_chart_title')}</h2>
                <span className="hint">{t('stats.elo_note')}</span>
              </div>
              {charts.status !== 'success' ? (
                <ChartBox role="status" aria-label={t('common.loading')}>
                  <Skeleton $h={240} $r={12} />
                </ChartBox>
              ) : eloData.length === 0 ? (
                <Empty>{t('stats.chart_empty')}</Empty>
              ) : eloData.length === 1 ? (
                <Empty>{t('stats.single_point', { elo: eloData[0].elo, date: eloData[0].date })}</Empty>
              ) : (
                <>
                  <ChartBox role="img" aria-label={`${t('stats.elo_chart_title')}. ${t('stats.elo_summary', { from: eloData[0].elo, to: eloData[eloData.length - 1].elo, n: eloData.length })}`}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={eloData} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                        <CartesianGrid vertical={false} stroke={theme.colors.border} />
                        <XAxis dataKey="date" stroke={theme.colors.textMuted} tickLine={false} minTickGap={32} tick={{ fontSize: 12 }} />
                        <YAxis stroke={theme.colors.textMuted} tickLine={false} axisLine={false} domain={['auto', 'auto']} width={44} tick={{ fontSize: 12 }} />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Line type="linear" dataKey="elo" name={t('stats.legend_elo')} stroke={theme.colors.primary} strokeWidth={2.5} dot={{ r: 3, fill: theme.colors.primary }} activeDot={{ r: 5 }} isAnimationActive={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartBox>
                  <p className="sr-only">{t('stats.elo_summary', { from: eloData[0].elo, to: eloData[eloData.length - 1].elo, n: eloData.length })}</p>
                </>
              )}
            </Surface>

            <Surface>
              <div className="head">
                <h2>{t('stats.outcomes')}</h2>
                {total > 0 && <span className="hint">{t('stats.outcome_of', { n: total })}</span>}
              </div>
              {summary.status !== 'success' ? (
                <Skeleton $h={120} $r={12} />
              ) : total === 0 ? (
                <Empty>{t('stats.chart_empty')}</Empty>
              ) : (
                <Outcomes>
                  <div className="bar" aria-hidden="true">
                    {seg.filter((x) => x.n > 0).map((x) => (
                      <span key={x.key} style={{ flex: x.n, background: x.color }} />
                    ))}
                  </div>
                  <ul>
                    {seg.map((x) => (
                      <li key={x.key}>
                        <i style={{ background: x.color }} aria-hidden="true" />
                        <span>{x.label}</span>
                        <span className="n">{x.n}</span>
                        <span className="p">{Math.round((x.n / total) * 100)}%</span>
                      </li>
                    ))}
                  </ul>
                </Outcomes>
              )}
            </Surface>
          </Split>

          <Surface aria-labelledby="stats-history">
            <div className="head">
              <h2 id="stats-history">{t('stats.table.title')}</h2>
              <span className="hint">{pageData ? t('common.shown_of', { shown: pageData.items.length, total: pageData.total }) : t('stats.history_scope')}</span>
            </div>
            {list.status === 'error' ? (
              <StateBlock kind="error" title={t('home.history_error')} onAction={list.reload} />
            ) : list.status !== 'success' || !pageData ? (
              <div role="status" aria-label={t('common.loading')} style={{ display: 'grid', gap: 8 }}>
                {[0, 1, 2, 3].map((i) => (
                  <Skeleton key={i} $h={68} $r={12} />
                ))}
              </div>
            ) : pageData.items.length === 0 ? (
              <StateBlock
                kind="empty"
                title={t('stats.filtered_empty')}
                text={query.q ? t('stats.table.empty_for', { value: query.q }) : undefined}
                actionLabel={t('stats.reset_filters')}
                onAction={dirty ? reset : undefined}
              />
            ) : (
              <Rows>
                {pageData.items.map((m) => {
                  const o = matchOutcome(m);
                  const own: [number, number] = m.team === 'T' ? [m.score[1], m.score[0]] : [m.score[0], m.score[1]];
                  const details = [f.date(m.createdAt), m.mode, m.duration !== null ? formatDuration(m.duration) : null].filter(Boolean).join(' · ');
                  return (
                    <MatchRow
                      key={m.id}
                      to={'/match/' + m.id}
                      map={m.map || null}
                      title={matchTitle(t, m.map, m.status)}
                      date={details}
                      outcome={m.status === 'cancelled' ? 'cancelled' : o}
                      stateLabel={matchState(t, m.status, o)}
                      score={o === 'pending' ? null : own}
                    />
                  );
                })}
              </Rows>
            )}
            {pageData && pageData.totalPages > 1 && (
              <Pager>
                <span>{t('common.page_of', { current: query.page, total: pageData.totalPages })}</span>
                <span className="btns">
                  <Action type="button" disabled={query.page <= 1} onClick={() => setQuery((q) => ({ ...q, page: q.page - 1 }))}>
                    {t('common.back')}
                  </Action>
                  <Action type="button" disabled={query.page >= pageData.totalPages} onClick={() => setQuery((q) => ({ ...q, page: q.page + 1 }))}>
                    {t('common.next')}
                  </Action>
                </span>
              </Pager>
            )}
          </Surface>

          <Split>
            <Surface>
              <div className="head">
                <h2>{t('stats.maps_title')}</h2>
                <span className="hint">{t('stats.maps_hint')}</span>
              </div>
              {charts.status !== 'success' ? (
                <Skeleton $h={120} $r={12} />
              ) : mapRows.length === 0 ? (
                <Empty>{t('stats.chart_empty')}</Empty>
              ) : (
                <MapRows>
                  {mapRows.map((m) => (
                    <li key={m.id}>
                      <MapThumb map={m.id} />
                      <div className="name">
                        {m.name}
                        <small>
                          {m.n} {matchesWord(m.n)}
                          {m.n < 3 && ` · ${t('stats.small_sample')}`}
                        </small>
                      </div>
                      <div className="meter">
                        {m.rate}%
                        <i aria-hidden="true">
                          <b style={{ width: `${m.rate}%` }} />
                        </i>
                      </div>
                    </li>
                  ))}
                </MapRows>
              )}
            </Surface>
            <Surface>
              <div className="head">
                <h2>{t('stats.activity_title')}</h2>
              </div>
              <p className="hint" style={{ marginTop: -8, marginBottom: 12 }}>
                {t('stats.activity_hint')}
              </p>
              {charts.status !== 'success' ? (
                <Skeleton $h={160} $r={12} />
              ) : dayData.length === 0 ? (
                <Empty>{t('stats.chart_empty')}</Empty>
              ) : (
                <>
                  <ChartBox style={{ height: 200 }} role="img" aria-label={`${t('stats.activity_title')}. ${t('stats.activity_summary', { n: dayData.reduce((a, d) => a + d.matches, 0), days: dayData.length })}`}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dayData} margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
                        <CartesianGrid vertical={false} stroke={theme.colors.border} />
                        <XAxis dataKey="date" stroke={theme.colors.textMuted} tickLine={false} minTickGap={24} tick={{ fontSize: 12 }} />
                        <YAxis stroke={theme.colors.textMuted} tickLine={false} axisLine={false} allowDecimals={false} width={28} tick={{ fontSize: 12 }} />
                        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: '#ffffff0a' }} />
                        <Bar dataKey="matches" name={t('stats.total_matches')} fill={theme.colors.primary} radius={[4, 4, 0, 0]} isAnimationActive={false} />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartBox>
                </>
              )}
            </Surface>
          </Split>
        </>
      )}

      {sheet && (
        <Dialog
          size="sm"
          label={t('stats.filters')}
          onClose={() => setSheet(false)}
          footer={
            <>
              <Action type="button" $variant="ghost" onClick={reset} disabled={!dirty}>
                {t('stats.reset_filters')}
              </Action>
              <Action type="button" $variant="primary" onClick={() => setSheet(false)}>
                {t('stats.show_results', { n: pageData?.total ?? 0 })}
              </Action>
            </>
          }
        >
          <h2>{t('stats.filters')}</h2>
          <Filters as="div">
            <div style={{ display: 'grid', gap: 16 }} className="field-stack">
              {filterFields}
            </div>
          </Filters>
        </Dialog>
      )}
    </Page>
  );
};

export default Stats;
